import { useState, useEffect } from 'react';

/**
 * Custom hook to monitor internet connectivity status
 * Uses the browser's navigator.onLine API and online/offline events
 * 
 * @returns {boolean} - true if online, false if offline
 */
export function useOnlineStatus(): boolean {
  const [isOnline, setIsOnline] = useState<boolean>(() => {
    // Initialize with current navigator.onLine status
    if (typeof window !== 'undefined' && 'navigator' in window) {
      return navigator.onLine;
    }
    return true; // Default to online if navigator is not available
  });

  useEffect(() => {
    // Handle online event
    const handleOnline = (): void => {
      console.log('[Network] Connection restored');
      setIsOnline(true);
    };

    // Handle offline event
    const handleOffline = (): void => {
      console.log('[Network] Connection lost');
      setIsOnline(false);
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}
